import React from 'react';
import { Col, Row, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '~/hooks/useScreenSizes';

function Disclousure(props: any) {
  const { data } = props;
  const { t } = useTranslation('ksaApplication');
  const isMobile = useIsMobile();

  const hasFamilyMembers = data?.companyQuestionnaire?.authorizedRepresentative?.hasFamilyMembers;
  const familyMemberName = data?.companyQuestionnaire?.authorizedRepresentative?.familyMemberName;
  const hasDealings = data?.companyQuestionnaire?.sanction?.hasDealings;
  const briefDescription = data?.companyQuestionnaire?.sanction?.briefDescription;

  return (
    <DisclosureWrapper>
      <DisclosureTitle level={4}>
        {t('Disclosure')}
      </DisclosureTitle>
      <Box isMobile={isMobile}>
        {/* ── PEP Question ── */}
        <Row gutter={[16, 8]}>
          <Col span={24}>
            <Typography.Paragraph className="labelKey">
              {t(
                'Are you, or a direct family member, or any individual associated with your business, a Politically Exposed Person (PEP)?'
              )}
            </Typography.Paragraph>
          </Col>
          <Col span={24}>
            <Typography.Paragraph className="value">
              {hasFamilyMembers ? t('Yes') : t('No')}
            </Typography.Paragraph>
          </Col>
        </Row>

        {hasFamilyMembers && (
          <FamilyRow gutter={[16, 8]}>
            <Col span={24}>
              <Typography.Paragraph className="labelKey">
                {t('Name of Politically Exposed Person')}
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <Typography.Paragraph className="value">
                {familyMemberName}
              </Typography.Paragraph>
            </Col>
          </FamilyRow>
        )}

        {/* ── Sanctions Question ── */}
        <SanctionRow gutter={[16, 8]}>
          <Col span={24}>
            <Typography.Paragraph className="labelKey labelKey--spaced">
              {t(
                'Do your business activities, owners, or officials have any direct or indirect involvement with, or interests in, any sanctioned countries, territories, or individuals/entities listed on sanctions lists?'
              )}
            </Typography.Paragraph>
            <Typography.Paragraph className="labelKey">
              {t(
                'This includes any current operations, offices, financial relationships, or planned engagements that may contravene applicable sanctions regulations.'
              )}
            </Typography.Paragraph>
          </Col>
          <Col span={24}>
            <Typography.Paragraph className="value">
              {hasDealings ? t('Yes') : t('No')}
            </Typography.Paragraph>
          </Col>
        </SanctionRow>

        {hasDealings && briefDescription && (
          <BriefRow gutter={[16, 8]}>
            <Col span={24}>
              <Typography.Paragraph className="labelKey">
                {t(
                  'Please provide the countries, individuals, business activities, goods/services involved (including their use, origin, and destination).'
                )}
              </Typography.Paragraph>
            </Col>
            <Col span={24}>
              <DescriptionBlock>
                {briefDescription.split('\n').map((line: string, idx: number) => (
                  <Typography.Paragraph key={idx} className="descLine">
                    {line}
                  </Typography.Paragraph>
                ))}
              </DescriptionBlock>
            </Col>
          </BriefRow>
        )}
      </Box>
    </DisclosureWrapper>
  );
}

const Box = styled.div<any>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  background-color: ${({ isMobile }) => (isMobile ? '#ffffff' : '#F9F9F9')};
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid #F5F5F5;
  box-shadow: 0 2px 6px 0 rgba(0, 89, 150, 0.03), 0 -1px 8px -10px rgba(0, 89, 150, 0.03);

  .labelKey {
    color: #000;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 24px;
    letter-spacing: 0.14px;
    margin-bottom: 0px;
  }

  .value {
    color: #000;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    margin-top: 2px;
    margin-bottom: 0;
  }
  .labelKey--spaced {
    margin-bottom: 4px;
  }
`;

const DescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  .descLine {
    color: #000;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    margin-bottom: 0 !important;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const DisclosureWrapper = styled.div`
  margin-top: 24px;
`;

const DisclosureTitle = styled(Typography.Title)`
  margin-bottom: 15px !important;
`;

const FamilyRow = styled(Row)`
  margin-top: 12px;
`;

const SanctionRow = styled(Row)`
  margin-top: 16px;
`;

const BriefRow = styled(Row)`
  margin-top: 12px;
`;

export default Disclousure;
